<template>
  <div class="login-wrapper">
    <!-- ══════════════════════════════════════════════════════════════════════
         SISI KIRI: HERO BRANDING (Tampil di Desktop & Layar Lebar >= 992px)
         ══════════════════════════════════════════════════════════════════════ -->
    <div class="login__hero d-none d-lg-flex">
      <div class="hero__glow-1" aria-hidden="true"></div>
      <div class="hero__glow-2" aria-hidden="true"></div>
      <div class="hero__pattern" aria-hidden="true"></div>

      <div class="hero__header">
        <router-link to="/" class="hero__brand-link">
          <BrandLogo size="lg" />
        </router-link>
      </div>

      <div class="hero__body">
        <div class="hero__badge">
          <i class="bi bi-shield-check text-warning"></i>
          <span>Official Tournament Platform</span>
        </div>

        <h2 class="hero__title">
          Platform Manajemen Turnamen & Kejuaraan Olahraga Digital
        </h2>
        <p class="hero__desc">
          Satu ekosistem terpadu untuk registrasi kontingen, pengundian bagan otomatis, jadwal gelanggang, hingga rekapitulasi medali dan tagihan secara real-time.
        </p>

        <div class="hero__features">
          <div class="hero__feature-item">
            <div class="feature-icon">
              <i class="bi bi-people-fill"></i>
            </div>
            <div class="feature-text">
              <div class="feature-title">Pendaftaran & Data Atlet Terpadu</div>
              <div class="feature-desc">Pengelolaan kuota atlet, kategori tanding & seni dengan verifikasi berkas cepat.</div>
            </div>
          </div>

          <div class="hero__feature-item">
            <div class="feature-icon">
              <i class="bi bi-diagram-3-fill"></i>
            </div>
            <div class="feature-text">
              <div class="feature-title">Generator Bagan & Jadwal Otomatis</div>
              <div class="feature-desc">Pengundian bagan adil dan pembagian gelanggang pertandingan tanpa bentrok.</div>
            </div>
          </div>

          <div class="hero__feature-item">
            <div class="feature-icon">
              <i class="bi bi-receipt-cutoff"></i>
            </div>
            <div class="feature-text">
              <div class="feature-title">Faktur, Kwitansi & Laporan Finansial</div>
              <div class="feature-desc">Pantau status pelunasan kontingen, verifikasi bukti bayar, dan unduh invoice resmi.</div>
            </div>
          </div>
        </div>
      </div>

      <div class="hero__footer">
        <span>&copy; Arena Sport Digital. Hak Cipta Dilindungi.</span>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════════════════════
         SISI KANAN: FORMULIR LOGIN (Responsif di PC, Tablet & Mobile)
         ══════════════════════════════════════════════════════════════════════ -->
    <div class="login__pane">
      <div class="login__card ui-card">
        <div class="login__header">
          <!-- Logo untuk perangkat mobile / tablet -->
          <router-link to="/" class="login__brand d-inline-flex justify-content-center d-lg-none">
            <BrandLogo size="md" />
          </router-link>
          <h1 class="login__title">{{ $t('auth.loginTitle') }}</h1>
          <p class="login__subtitle">{{ $t('auth.loginSubtitle') }}</p>
        </div>

        <form class="login__form" novalidate @submit.prevent="handleLogin">
          <!-- Ringkasan galat -->
          <div v-if="errorMessage" class="login__alert" role="alert">
            <i class="bi bi-exclamation-circle" aria-hidden="true"></i>
            <span>{{ errorMessage }}</span>
          </div>

          <div class="ui-field">
            <label for="login-email" class="ui-label">{{ $t('auth.email') }}</label>
            <div class="input-icon-wrap">
              <i class="bi bi-envelope input-icon text-muted"></i>
              <input
                id="login-email"
                v-model.trim="email"
                type="email"
                class="ui-input has-icon"
                autocomplete="email"
                required
                :placeholder="$t('auth.emailPlaceholder')"
                :aria-invalid="Boolean(fieldErrors.email)"
                :aria-describedby="fieldErrors.email ? 'login-email-error' : undefined"
              />
            </div>
            <p v-if="fieldErrors.email" id="login-email-error" class="ui-error">
              {{ fieldErrors.email }}
            </p>
          </div>

          <div class="ui-field">
            <label for="login-password" class="ui-label">{{ $t('auth.password') }}</label>
            <div class="login__password input-icon-wrap">
              <i class="bi bi-lock input-icon text-muted"></i>
              <input
                id="login-password"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                class="ui-input has-icon"
                autocomplete="current-password"
                required
                :placeholder="$t('auth.passwordPlaceholder')"
                :aria-invalid="Boolean(fieldErrors.password)"
                :aria-describedby="fieldErrors.password ? 'login-password-error' : undefined"
              />
              <button
                type="button"
                class="login__password-toggle"
                :aria-label="showPassword ? $t('auth.hidePassword') : $t('auth.showPassword')"
                @click="showPassword = !showPassword"
              >
                <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'" aria-hidden="true"></i>
              </button>
            </div>
            <p v-if="fieldErrors.password" id="login-password-error" class="ui-error">
              {{ fieldErrors.password }}
            </p>
          </div>

          <button
            type="submit"
            class="ui-btn ui-btn--primary ui-btn--lg ui-btn--block"
            :disabled="loading"
          >
            <span v-if="loading" class="ui-spinner" aria-hidden="true"></span>
            <span>{{ loading ? $t('common.loading') : $t('auth.loginButton') }}</span>
          </button>
        </form>

        <router-link to="/admin/lupa-sandi" class="login__lupa">Lupa kata sandi?</router-link>

        <div class="login__footer">
          <router-link to="/" class="login__back">
            <i class="bi bi-arrow-left" aria-hidden="true"></i>
            {{ $t('common.back') }}
          </router-link>

          <div class="login__prefs">
            <LanguageSwitcher />
            <ThemeToggle />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { halamanAwal } from '@/utils/peran';
import BrandLogo from '@/components/ui/BrandLogo.vue';
import { useToast } from 'vue-toastification';
import http, { setToken } from '@/services/http';
import ThemeToggle from '@/components/ui/ThemeToggle.vue';
import LanguageSwitcher from '@/components/ui/LanguageSwitcher.vue';

export default {
  name: 'LoginPage',


  components: { BrandLogo, ThemeToggle, LanguageSwitcher },

  data() {
    return {
      email: '',
      password: '',
      showPassword: false,
      errorMessage: '',
      fieldErrors: {},
      loading: false,
    };
  },

  mounted() {
    document.title = this.$t('auth.loginTitle') || 'Masuk';
  },

  methods: {
    validate() {
      this.fieldErrors = {};

      if (!this.email) {
        this.fieldErrors.email = this.$t('validation.required', {
          field: this.$t('auth.email'),
        });
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email)) {
        this.fieldErrors.email = this.$t('validation.email');
      }

      if (!this.password) {
        this.fieldErrors.password = this.$t('validation.required', {
          field: this.$t('auth.password'),
        });
      }

      return Object.keys(this.fieldErrors).length === 0;
    },

    async handleLogin() {
      this.errorMessage = '';

      // Validasi di sisi klien lebih dulu — tidak perlu memanggil server
      // hanya untuk mengetahui bahwa kolom masih kosong.
      if (!this.validate()) return;

      this.loading = true;
      const toast = useToast();

      try {
        const { data } = await http.post('/login', {
          email: this.email,
          password: this.password,
        });

        setToken(data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));

        /*
         * Store Vuex ikut diperbarui. Versi lama hanya menulis ke
         * localStorage, sehingga getter isAuthenticated tetap bernilai false
         * dan header masih menampilkan tombol "Masuk" sampai halaman dimuat ulang.
         */
        this.$store.dispatch('setAuthToken', data.access_token);
        this.$store.dispatch('setUser', data.user);

        toast.success(this.$t('auth.loginSuccess'));

        /*
         * Official diarahkan ke KARTU ACARA, bukan dasbor panitia.
         *
         * Yang pertama dilakukan official setelah masuk selalu sama: memilih
         * acara mana yang diikuti, lalu mengurus kontingennya di situ.
         * Dasbor berisi ringkasan penyelenggaraan yang bukan urusannya, dan
         * dari sana tidak ada jalan yang jelas menuju acaranya sendiri.
         *
         * Panitia (Owner / Event PIC) tetap ke dasbor — bagi mereka
         * ringkasan itu memang halaman kerja utamanya.
         *
         * ?redirect= tetap dihormati: pengguna yang tadinya hendak membuka
         * satu halaman lalu diminta masuk harus kembali ke halaman itu,
         * bukan dibuang ke tempat lain.
         */
        // Bendahara langsung ke Keuangan; aturannya ada di utils/peran.js.
        const bawaan = halamanAwal();

        this.$router.push(this.$route.query.redirect || bawaan);
      } catch (error) {
        const status = error.response?.status;

        if (status === 429) {
          this.errorMessage = this.$t('auth.tooManyAttempts');
        } else if (status === 401) {
          this.errorMessage = this.$t('auth.invalidCredentials');
        } else if (error.validationErrors) {
          this.fieldErrors = Object.fromEntries(
            Object.entries(error.validationErrors).map(([key, messages]) => [
              key,
              Array.isArray(messages) ? messages[0] : messages,
            ])
          );
        } else {
          this.errorMessage = error.friendlyMessage || this.$t('common.errorDesc');
        }

        if (this.errorMessage) toast.error(this.errorMessage);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ── Wadah Utama ── */
.login-wrapper {
  display: flex;
  min-height: 100vh;
  width: 100%;
  background-color: var(--bg-base);
  overflow-x: hidden;
}

/* ── Sisi Kiri: Hero Branding (Desktop >= 992px) ── */
.login__hero {
  position: relative;
  flex: 1.15;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 3rem 3.5rem;
  overflow-y: auto;
  overflow-x: clip;
  background: linear-gradient(145deg, #090d16 0%, #111827 50%, #1c1411 100%);
  color: #ffffff;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
}

.hero__glow-1 {
  position: absolute;
  top: -100px;
  left: -100px;
  width: 480px;
  height: 480px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(240, 80, 8, 0.22) 0%, transparent 70%);
  pointer-events: none;
}

.hero__glow-2 {
  position: absolute;
  bottom: -80px;
  right: -80px;
  width: 420px;
  height: 420px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(234, 88, 12, 0.15) 0%, transparent 70%);
  pointer-events: none;
}

.hero__pattern {
  position: absolute;
  inset: 0;
  opacity: 0.04;
  background-image: radial-gradient(#ffffff 1px, transparent 1px);
  background-size: 24px 24px;
  pointer-events: none;
}

.hero__header {
  position: relative;
  z-index: 2;
}

.hero__brand-link {
  display: inline-block;
  text-decoration: none;
}

.hero__body {
  position: relative;
  z-index: 2;
  margin: 2.5rem 0;
  max-width: 580px;
}

.hero__badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0.85rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  color: #fed7aa;
  margin-bottom: 1.5rem;
  backdrop-filter: blur(8px);
}

.hero__title {
  font-family: var(--font-display);
  font-size: 2.25rem;
  font-weight: 800;
  line-height: 1.25;
  letter-spacing: -0.02em;
  color: #ffffff;
  margin-bottom: 1rem;
}

.hero__desc {
  font-size: 1rem;
  line-height: 1.6;
  color: #94a3b8;
  margin-bottom: 2.5rem;
}

.hero__features {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.hero__feature-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.feature-icon {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: rgba(240, 80, 8, 0.15);
  border: 1px solid rgba(240, 80, 8, 0.3);
  color: #fb923c;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.feature-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #f8fafc;
  margin-bottom: 0.15rem;
}

.feature-desc {
  font-size: 0.825rem;
  line-height: 1.45;
  color: #94a3b8;
}

.hero__footer {
  position: relative;
  z-index: 2;
  font-size: 0.8125rem;
  color: #64748b;
}

/* ── Sisi Kanan: Formulir Login ── */
.login__pane {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2.5rem 2rem;
  background-color: var(--bg-base);
  overflow-y: auto;
  background-image: radial-gradient(
    circle at 50% 0%,
    var(--accent-subtle) 0%,
    transparent 60%
  );
}

.login__card {
  width: 100%;
  max-width: 440px;
  min-width: 320px;
  padding: 2.5rem 2.25rem;
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-xl);
  background-color: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  overflow: visible;
}

.login__header {
  text-align: center;
  margin-bottom: var(--space-6);
}

.login__brand {
  margin-bottom: var(--space-4);
}

.login__title {
  font-family: var(--font-display);
  font-size: var(--text-2xl);
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--text-primary);
  margin-bottom: var(--space-2);
}

.login__subtitle {
  margin: 0;
  font-size: var(--text-sm);
  color: var(--text-secondary);
  line-height: 1.5;
}

.login__form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.login__alert {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  font-size: var(--text-sm);
  color: var(--danger-500);
  background-color: rgb(239 68 68 / 0.1);
  border: 1px solid rgb(239 68 68 / 0.25);
  border-radius: var(--radius-md);
}

.input-icon-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 0.85rem;
  font-size: 1.05rem;
  pointer-events: none;
  z-index: 2;
}

.ui-input.has-icon {
  padding-left: 2.65rem;
}

.login__password {
  position: relative;
}

.login__password .ui-input.has-icon {
  padding-right: 2.75rem;
}

.login__password-toggle {
  position: absolute;
  top: 50%;
  right: var(--space-2);
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: var(--text-muted);
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  z-index: 2;
}

.login__password-toggle:hover {
  color: var(--text-primary);
}

.login__lupa {
  display: block;
  margin-top: var(--space-2);
  font-size: var(--text-sm);
  font-weight: 500;
  text-align: right;
  color: var(--text-secondary);
  text-decoration: none;
  transition: color var(--transition-fast);
}

.login__lupa:hover {
  color: var(--accent);
  text-decoration: underline;
}

.login__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  margin-top: var(--space-6);
  padding-top: var(--space-4);
  border-top: 1px solid var(--border-subtle);
}

.login__back {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--text-secondary);
  text-decoration: none;
  transition: color var(--transition-fast);
}

.login__back:hover {
  color: var(--accent);
}

.login__prefs {
  display: flex;
  align-items: center;
  gap: var(--space-1);
}

/* ── Layar Tablet & Laptop Kecil ── */
@media (max-width: 1199px) and (min-width: 992px) {
  .login__hero {
    padding: 3rem 2.5rem;
  }
  .hero__title {
    font-size: 1.85rem;
  }
}

/* ── Layar Mobile & Tablet (< 992px) ── */
@media (max-width: 991px) {
  .login__pane {
    flex: 1;
    width: 100%;
    padding: var(--space-5);
  }
}

@media (max-width: 480px) {
  .login__pane {
    padding: var(--space-3);
  }

  .login__card {
    padding: 1.75rem 1.25rem;
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-md);
  }

  .login__header {
    margin-bottom: var(--space-4);
  }

  .login__title {
    font-size: 1.35rem;
  }

  .login__subtitle {
    font-size: 0.8125rem;
  }

  .login__footer {
    margin-top: var(--space-4);
    padding-top: var(--space-3);
  }
}
</style>
